import type { initialize } from "@sumologic/opentelemetry-rum";

import type { LpStage, LpStamp, LpTrace } from "../../webapp/calcs/lib/domain";
import type { LpStageListener } from "../../webapp/calcs/main/trace";
import { getSumoLogicRum } from "./integrations/sumologic";

type Rum = Pick<NonNullable<ReturnType<typeof initialize>>, "tracer" | "api">;
type Span = ReturnType<Rum["tracer"]["startSpan"]>;

const ROOT_TTL_MS = 5 * 60_000;

const PREVIOUS: Record<Exclude<LpStage, "arrived">, LpStage> = {
  calculated: "arrived",
  main: "calculated",
  painted: "main",
};

/**
 * Both ends of a span on one clock: server-aligned when both stamps have it, else the client's wall
 * clock for both. Mixing the two would fold the client's skew into the duration.
 */
const bounds = (from: LpStamp, to: LpStamp) =>
  from.server !== null && to.server !== null
    ? { start: from.server, end: to.server, synced: true }
    : { start: from.client, end: to.client, synced: false };

/**
 * Turns trace stages into OpenTelemetry spans on the RUM tracer: `lp.arrived` is the root (market
 * timestamp to arrival), every later stage a child spanning the previous stage to itself. Each span
 * is created and ended in one go, so a stage is exported as soon as it happens.
 */
export const createLivePriceSpanMinter = (rum: Rum): LpStageListener => {
  const roots = new Map<string, { span: Span; since: number }>();

  const attributes = (trace: LpTrace, stamp: LpStamp, stage: LpStage, synced: boolean) => ({
    "lp.stage": stage,
    "lp.trace_id": trace.backendTraceId,
    "lp.row_count": trace.rowCount,
    ...(trace.version !== undefined ? { "lp.version": trace.version } : {}),
    "lp.clock_synced": synced,
    "lp.ts.market": trace.marketTs,
    "lp.ts.client": stamp.client,
    "lp.ts.mono": stamp.mono,
    "lp.ts.origin": stamp.origin,
  });

  const evict = () => {
    const cutoff = performance.now() - ROOT_TTL_MS;
    for (const [id, root] of roots) if (root.since < cutoff) roots.delete(id);
  };

  return (trace, stage) => {
    const stamp = trace.ts[stage];
    if (!stamp) return;

    if (stage === "arrived") {
      evict();
      // The market timestamp is server time, so an unsynced arrival cannot be put on one clock.
      const synced = stamp.server !== null;
      const span = rum.tracer.startSpan("lp.arrived", {
        startTime: trace.marketTs,
        attributes: attributes(trace, stamp, stage, synced),
      });
      span.end(stamp.server ?? stamp.client);
      roots.set(trace.id, { span, since: performance.now() });
      return;
    }

    const root = roots.get(trace.id);
    const previous = trace.ts[PREVIOUS[stage]];
    if (!root || !previous) return;
    const { start, end, synced } = bounds(previous, stamp);
    const span = rum.tracer.startSpan(
      `lp.${stage}`,
      { startTime: start, attributes: attributes(trace, stamp, stage, synced) },
      rum.api.trace.setSpan(rum.api.context.active(), root.span),
    );
    span.end(end);
    if (stage === "painted") roots.delete(trace.id);
  };
};

/** Mint spans for the worker's live-price traces; no-op where RUM is not initialised. */
export const attachLivePriceTracing = (worker: { trace: { onStage: (cb: LpStageListener) => () => void } }) => {
  const rum = getSumoLogicRum();
  if (!rum) return () => {};
  return worker.trace.onStage(createLivePriceSpanMinter(rum));
};
